/**
 * CSRF token cache for cross-origin SPA → API calls.
 *
 * The API sets csrf_token on the api.* host. www.* cannot read a host-only
 * api cookie from document.cookie. We cache the token from response headers /
 * csrf-token API in sessionStorage and send it as X-CSRF-Token.
 *
 * Do NOT prefer document.cookie when the SPA and API are cross-origin: a stale
 * Domain=.parent cookie can disagree with the host-only api cookie the browser
 * actually sends, which produces "CSRF token validation failed" (403).
 */
import axios from "axios";
import { resolveApiClientBaseUrl } from "../config/env";

const CSRF_SESSION_KEY = "api_csrf_token";

function readCsrfCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const cookies = document.cookie.split(";");
  const csrfCookie = cookies.find((cookie) =>
    cookie.trim().startsWith("csrf_token=")
  );
  if (!csrfCookie) {
    return null;
  }
  return csrfCookie.split("=")[1] ?? null;
}

/** True when SPA origin differs from API origin (www → api). */
function isCrossOriginApi(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const base = resolveApiClientBaseUrl();
  // Empty base = Vite proxy / same-origin.
  if (!base) {
    return false;
  }
  try {
    return new URL(base).origin !== window.location.origin;
  } catch {
    return true;
  }
}

export function getCachedCsrfToken(): string | null {
  // Same-origin only: cookie is what the browser will send.
  if (!isCrossOriginApi()) {
    const fromCookie = readCsrfCookie();
    if (fromCookie) {
      try {
        sessionStorage.setItem(CSRF_SESSION_KEY, fromCookie);
      } catch {
        // ignore quota / private mode
      }
      return fromCookie;
    }
  }

  try {
    return sessionStorage.getItem(CSRF_SESSION_KEY);
  } catch {
    return null;
  }
}

export function setCachedCsrfToken(token: string): void {
  try {
    sessionStorage.setItem(CSRF_SESSION_KEY, token);
  } catch {
    // ignore quota / private mode
  }
}

export function clearCachedCsrfToken(): void {
  try {
    sessionStorage.removeItem(CSRF_SESSION_KEY);
  } catch {
    // ignore
  }
}

/** Attach X-CSRF-Token to state-changing axios requests when cached. */
export function applyCsrfRequestHeader(
  config: { method?: string; headers?: Record<string, unknown> }
): void {
  const method = config.method?.toLowerCase() ?? "";
  if (!["post", "put", "delete", "patch"].includes(method)) {
    return;
  }
  const token = getCachedCsrfToken();
  if (token && config.headers) {
    config.headers["X-CSRF-Token"] = token;
  }
}

/** Persist X-CSRF-Token from any API response for subsequent POSTs. */
export function captureCsrfResponseHeader(
  headers: Record<string, unknown> | undefined
): void {
  if (!headers) return;
  const raw =
    headers["x-csrf-token"] ??
    headers["X-CSRF-Token"];
  if (typeof raw === "string" && raw.length > 0) {
    setCachedCsrfToken(raw);
  }
}

function tokenFromCsrfResponse(response: {
  data?: { csrf_token?: string };
  headers?: Record<string, unknown>;
}): string | null {
  const headers = response.headers ?? {};
  const raw =
    headers["x-csrf-token"] ??
    headers["X-CSRF-Token"];
  if (typeof raw === "string" && raw.length > 0) {
    return raw;
  }
  const body = response.data?.csrf_token;
  return typeof body === "string" && body.length > 0 ? body : null;
}

let csrfBootstrapPromise: Promise<void> | null = null;
/** Bumped on force refresh so in-flight non-force fetches cannot write stale cache. */
let csrfBootstrapGeneration = 0;

export type EnsureCsrfTokenOptions = {
  /** Ignore cache and re-fetch from the API (e.g. after a CSRF 403). */
  force?: boolean;
};

function fetchAndCacheCsrfToken(generation: number): Promise<void> {
  const base = resolveApiClientBaseUrl();
  const url = base
    ? `${base}/api/v1/auth/csrf-token`
    : "/api/v1/auth/csrf-token";
  return axios
    .get<{ csrf_token?: string }>(url, { withCredentials: true })
    .then((response) => {
      if (generation !== csrfBootstrapGeneration) {
        return;
      }
      // Always persist the API-issued token. Do not re-read document.cookie
      // afterward — that can overwrite with a stale Domain=.parent value.
      const token = tokenFromCsrfResponse(
        response as {
          data?: { csrf_token?: string };
          headers?: Record<string, unknown>;
        }
      );
      if (token) {
        setCachedCsrfToken(token);
      }
    });
}

/** Fetch CSRF token from API if not already cached (idempotent). */
export async function ensureCsrfToken(
  options?: EnsureCsrfTokenOptions
): Promise<void> {
  if (options?.force) {
    clearCachedCsrfToken();
    csrfBootstrapGeneration += 1;
    csrfBootstrapPromise = null;
  } else if (getCachedCsrfToken()) {
    return;
  }

  const generation = csrfBootstrapGeneration;
  if (!csrfBootstrapPromise) {
    const pending = fetchAndCacheCsrfToken(generation).finally(() => {
      if (csrfBootstrapPromise === pending) {
        csrfBootstrapPromise = null;
      }
    });
    csrfBootstrapPromise = pending;
  }
  await csrfBootstrapPromise;
}
